import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import { useStaticQuery, graphql, Link } from 'gatsby';
import Img from 'gatsby-image';
import SEO from '../../seo';

import CollectionGridItem from '../../collection-components/collection-grid-item';
import { media, TransitionMixin } from '../../helpers';
import Blob from '../../../images/page-images/blob-3.inline.svg';
import LeftArrow from '../../../images/icons/arrow-left.inline.svg';
import RightArrow from '../../../images/icons/arrow-right.inline.svg';
import '../../package-styles/slick.css';

const HomeBoxSliderContainer = styled.div`
    margin: 0 auto;
    padding-bottom: 40px;
    ${media.medium`max-width: 82.5vw; padding-bottom: 0px;`}

    .slider-container {
        position: relative;

        top: -10vh;
        margin-bottom: -10vh;
        ${media.large`top: -50vh;margin-bottom: -90vh;`}
        .arrows-container {
            position: absolute;
            bottom: 12vh;
            right: 2vw;
            display: none;
            z-index: 100;
            ${media.medium`display: block;`}
            ${media.large`bottom: 3vh;`}

            .inner-wrap {
                max-width: 200px;
                margin-left: auto;
                display: flex;

                .arrow-container {
                    &.prev {
                        margin-right: 100px;
                    }
                    &.next {
                        flex: 1;
                        text-align: right;
                    }

                    button {
                        background-color: transparent;
                        border: none;
                    }
                }
            }
        }

        .mobile-arrows-container {
            position: absolute;
            right: 7vw;
            top: 1.5vh;
            ${media.medium`display: none;`}
            .inner-wrap {
                max-width: 200px;
                margin-left: auto;
                display: flex;

                .arrow-container {
                    &.prev {
                        margin-right: 10px;
                    }
                    &.next {
                        flex: 1;
                        text-align: right;
                    }

                    button {
                        background-color: transparent;
                        border: none;

                        svg {
                            path {
                                fill: #000;
                            }
                        }
                    }
                }
            }
        }
    }
    .slide-collection-container {
        .collection-container {
            display: flex;
            flex-wrap: wrap;
            > * {
                flex: 0 0 50%;
                ${media.large`flex: 0 0 25%;`}
            }
        }
        .more-link {
            padding: 0;
            text-align: center;

            a {
                color: #000;

                &:hover {
                    opacity: 0.7;
                }
            }
        }
    }
`;
const SlideContainer = styled.div`
    .slide-image-container {
        position: relative;
        padding-bottom: 30px;
        margin-bottom: 15vh;
        ${media.large`margin-bottom: 0;`}
        .image-container {
            position: relative;
            max-height: 65vh;
            overflow: hidden;
            padding-bottom: 0vh;

            .gatsby-image-wrapper {
                min-height: 40vh;
            }
        }
        > .content-container {
            position: absolute;
            left: 0;
            width: 100%;
            bottom: -10vh;
            z-index: 100;

            ${media.large`bottom: 5vh;left: 5vw; width: auto;`}
            > .inner-wrap {
                max-width: 90vw;
                background-color: #f2f0eb;
                box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.25);
                padding: 70px 40px 40px;
                margin: 0 auto;
                ${media.medium`max-width: 500px; margin: 0; padding: 40px;`};

                h3 {
                    font-size: 1.6rem;
                    margin: 0 0 15px;
                    font-weight: 800;
                    font-family: neue-kabel, sans-serif;
                }

                p {
                    margin: 0 0 15px;
                    font-size: 1.1rem;
                    line-height: 1.5;
                    max-width: 400px;
                }

                a {
                    color: #f74d33;
                    font-size: 1rem;
                    font-weight: 800;
                    font-family: neue-kabel, sans-serif;
                    ${TransitionMixin('.25s')};

                    &:hover {
                        opacity: 0.5;
                    }
                }
            }
        }
    }
`;

const HomeBoxSlider = () => {
    const data = useStaticQuery(graphql`
        query {
            home: allWpPage(filter: { title: { eq: "Home" } }) {
                edges {
                    node {
                        id
                        home_acf {
                            bottomSliderSettings {
                                bottomSliderSlides {
                                    slideImage {
                                        localFile {
                                            childImageSharp {
                                                fluid(maxWidth: 1920) {
                                                    ...GatsbyImageSharpFluid
                                                }
                                            }
                                        }
                                    }
                                    slideCollectionHandle
                                    slideCaption
                                    slideTitle
                                    slideLink {
                                        title
                                        url
                                    }
                                }
                            }
                        }
                    }
                }
            }
            collections: allShopifyCollection {
                edges {
                    node {
                        title
                        handle
                        products {
                            options {
                                name
                                values
                            }
                            availableForSale
                            priceRange {
                                maxVariantPrice {
                                    amount
                                }
                            }
                            handle
                            images {
                                id
                                altText
                                localFile {
                                    childImageSharp {
                                        fluid(maxWidth: 500) {
                                            ...GatsbyImageSharpFluid
                                        }
                                    }
                                }
                            }
                            productType
                            shopifyId
                            tags
                            title
                            variants {
                                compareAtPrice
                                availableForSale
                                price
                                selectedOptions {
                                    name
                                    value
                                }
                                shopifyId
                                image {
                                    altText
                                    localFile {
                                        childImageSharp {
                                            fluid(maxWidth: 700) {
                                                ...GatsbyImageSharpFluid
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    `);

    const sliderRef = useRef(null);
    const [slideIndex, setSlideIndex] = useState(0);
    const [updateCount, setUpdateCount] = useState(0);

    let collections = data && data.collections.edges;

    let slides =
        data &&
        data.home.edges[0].node.home_acf.bottomSliderSettings
            .bottomSliderSlides;
    // let products = data && data.allShopifyCollection.edges[0].node.products;

    var settings = {
        dots: false,
        infinite: true,
        fade: true,
        speed: 500,
        arrows: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        afterChange: () => setUpdateCount(updateCount + 1),
        beforeChange: (current, next) => setSlideIndex(next),
    };

    function handleSlideChange(newIndex) {
        setSlideIndex(newIndex);
        sliderRef.current.slickGoTo(newIndex);
    }

    function NextArrow() {
        return (
            <div className="arrow-container next">
                <button onClick={() => handleSlideChange(slideIndex + 1)}>
                    <RightArrow />
                </button>
            </div>
        );
    }

    function PrevArrow() {
        return (
            <div className="arrow-container prev">
                <button onClick={() => handleSlideChange(slideIndex - 1)}>
                    <LeftArrow />
                </button>
            </div>
        );
    }

    return (
        <HomeBoxSliderContainer>
            <div className="slider-container">
                <Slider ref={sliderRef} {...settings}>
                    {slides.length > 0 &&
                        slides.map((slide) => {
                            const {
                                slideImage,
                                slideTitle,
                                slideCaption,
                                slideLink,
                                slideCollectionHandle,
                            } = slide || {};

                            function generateTitle() {
                                return { __html: slideTitle };
                            }

                            let products = collections.filter(
                                (collection) =>
                                    collection.node.handle ===
                                    slideCollectionHandle
                            );

                            products = products[0].node.products;

                            return (
                                <SlideContainer>
                                    <div className="slide-image-container">
                                        <div className="image-container">
                                            <Img
                                                fluid={
                                                    slideImage.localFile
                                                        .childImageSharp.fluid
                                                }
                                            />
                                            <div className="arrows-container">
                                                <div className="inner-wrap">
                                                    <PrevArrow />
                                                    <NextArrow />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="content-container">
                                            <div className="mobile-arrows-container">
                                                <div className="inner-wrap">
                                                    <PrevArrow />
                                                    <NextArrow />
                                                </div>
                                            </div>
                                            <div className="inner-wrap">
                                                <h3>{slideTitle}</h3>
                                                <p>{slideCaption}</p>
                                                <div className="btn-container">
                                                    <Link to={slideLink.url}>
                                                        {slideLink.title}
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="slide-collection-container">
                                        <div className="collection-container">
                                            {products &&
                                                products
                                                    .slice(0, 4)
                                                    .map((product) => {
                                                        return (
                                                            <CollectionGridItem
                                                                product={
                                                                    product
                                                                }
                                                            />
                                                        );
                                                    })}
                                        </div>
                                        <div className="more-link">
                                            <Link
                                                to={`/collections/${slideCollectionHandle}`}
                                            >
                                                Show More
                                            </Link>
                                        </div>
                                    </div>
                                </SlideContainer>
                            );
                        })}
                </Slider>
            </div>
        </HomeBoxSliderContainer>
    );
};

export default HomeBoxSlider;
